import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "../context/ContentContext";

/**
 * Reader's Notebook
 * A soft, editorial card at the end of each blog post inviting the reader to
 * the Travel Resources page. Headline & sub-line vary by the post's primary
 * category to feel handwritten rather than templated.
 *
 * Visual rules:
 *  - Cream paper background, hairline border, no shadow
 *  - Globezing logo at top-left
 *  - Serif heading, single elegant arrow CTA
 *  - Never feels like a banner ad
 */

// Pool of opening lines — selected deterministically per post by slug hash
const TEMPLATES = [
  (place) => `Planning your own ${place} journey?`,
  (place) => `Inspired by ${place}?`,
  (place) => `Thinking of visiting ${place}?`,
  (place) => `Dreaming of ${place}?`,
  (place) => `Drawn to ${place}?`,
];

const SUBLINES = [
  "Explore stays, experiences and travel tools →",
  "Discover stays, experiences and travel tools →",
  "Browse the resources that helped us along the way →",
  "Find the tools that make a journey easier →",
];

// Stable hash so the same post always shows the same line — keeps the
// site feeling intentional rather than randomized on each refresh.
function pickIndex(slug, modulo) {
  let h = 0;
  for (let i = 0; i < (slug || "").length; i++) {
    h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return h % modulo;
}

export default function ReadersNotebook({ post }) {
  const { getCategory } = useContent();

  const { heading, subline } = useMemo(() => {
    const slug = post?.slug || "";
    const primaryCatSlug = post?.categories?.[0];
    const cat = primaryCatSlug ? getCategory(primaryCatSlug) : null;
    const place = cat?.name || null;

    if (place) {
      const t = TEMPLATES[pickIndex(slug, TEMPLATES.length)];
      const s = SUBLINES[pickIndex(slug + "x", SUBLINES.length)];
      return { heading: t(place), subline: s };
    }
    return {
      heading: "Planning a journey of your own?",
      subline: "Browse the resources that helped us along the way →",
    };
  }, [post, getCategory]);

  return (
    <aside
      data-testid="readers-notebook"
      className="not-prose my-16 max-w-3xl mx-auto"
    >
      <Link
        to="/travel-resources"
        data-testid="readers-notebook-link"
        className="group relative block bg-[#F5EFE4] border border-stone-300/60 px-7 py-10 sm:px-12 sm:py-12 transition-colors hover:border-stone-500/60"
      >
        {/* paper-edge subtle grain */}
        <div className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #000 1px, transparent 1px), radial-gradient(circle at 70% 65%, #000 1px, transparent 1px)", backgroundSize: "8px 8px, 11px 11px" }} />

        <div className="relative flex items-start gap-5 sm:gap-7">
          <img
            src="/globezing-logo-256.png"
            srcSet="/globezing-logo-256.png 1x, /globezing-logo-512.png 2x"
            alt=""
            width="56"
            height="56"
            loading="lazy"
            className="hidden sm:block w-12 h-12 sm:w-14 sm:h-14 rounded-full object-contain ring-1 ring-stone-400/40 flex-shrink-0 mt-1"
          />

          <div className="flex-1 min-w-0">
            <p className="kicker text-stone-500 text-[10px]">From the Reader's Notebook</p>

            <h3 className="font-serif text-2xl sm:text-[28px] leading-[1.2] text-stone-900 mt-3 group-hover:text-[#9c4122] transition-colors">
              {heading}
            </h3>

            <p className="mt-4 text-stone-700 text-[15px] sm:text-base leading-relaxed">
              {subline.replace(/\s*→\s*$/, "")}
            </p>

            <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-semibold text-stone-900 group-hover:text-[#9c4122] transition-colors">
              Open Travel Resources
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </Link>
    </aside>
  );
}
